/**
 * Shared bootstrap for player and preview sample pages.
 * - Fetches client IP (for analytics)
 * - Builds experienceMetadata
 * - Optionally loads scripts in order
 */
import type { ExperienceMetadataInit } from "@/types/experience";

interface BootOptions {
  experienceId?: number | null;
  mapUrl?: string | null;
  mediaServer?: string;
  assetServer?: string;
  apiBaseUrl?: string;
  apiProtocol?: string;
  scripts?: BootScript[];
}

/** `module: true` loads the script as `type="module"` (play.js). */
interface BootScript {
  src: string;
  await?: boolean;
  module?: boolean;
}

interface MetadataOptions {
  experienceId?: number | null;
  mapUrl?: string | null;
  ipAddress?: string;
  assetServer: string;
  mediaServer: string;
  apiBaseUrl: string;
  apiProtocol: string;
}

((global: Window & typeof globalThis) => {
  const IPIFY_URL = "https://api.ipify.org?format=json";
  const DEFAULT_ASSET_SERVER = "assets.pixmoto.com";
  const DEFAULT_MEDIA_CDN = "media.pixmoto.com";

  function resolveAssetServer(explicit?: string): string {
    if (explicit) {
      return explicit;
    }
    if (
      typeof global.PIXMOTO_ASSETS_SERVER === "string" &&
      global.PIXMOTO_ASSETS_SERVER
    ) {
      return global.PIXMOTO_ASSETS_SERVER;
    }
    return DEFAULT_ASSET_SERVER;
  }

  function resolveMediaServer(explicit?: string): string {
    if (explicit) {
      return explicit;
    }
    const configured =
      typeof global.PIXMOTO_MEDIA_SERVER === "string"
        ? global.PIXMOTO_MEDIA_SERVER
        : "";
    if (configured) {
      const bare = configured.replace(/^\/+/, "");
      if (global.ENVIRONMENT === "production") {
        return bare;
      }
      if (configured.indexOf("://") >= 0) {
        return configured;
      }
      if (configured.indexOf(global.location.host) === 0) {
        return configured;
      }
      if (configured.charAt(0) === "/") {
        return global.location.host + configured;
      }
      if (configured.indexOf("proxy/") === 0) {
        return `${global.location.host}/${configured}`;
      }
      if (bare === DEFAULT_MEDIA_CDN) {
        return defaultMediaServerHost();
      }
      if (configured.indexOf(".") >= 0) {
        const hostname = global.location.hostname || global.location.host;
        if (hostUsesMediaProxy(hostname)) {
          return `${global.location.host}/proxy/${bare}`;
        }
        return bare;
      }
      return `${global.location.host}/${configured}`;
    }
    return defaultMediaServerHost();
  }

  /**
   * Experience ID from the host page URL (not the publisher API URL).
   * Path: prefers .../publisher/experience/{id}, else last positive integer segment
   * (e.g. /52980, /preview/desktop/52980).
   * Query: experience_id (preferred), expid, id (legacy).
   */
  function getExperienceIdFromUrl(): number | null {
    const path = window.location.pathname;
    const pubMatch = path.match(/\/publisher\/experience\/(\d+)/);
    if (pubMatch) {
      const fromPub = Number.parseInt(pubMatch[1], 10);
      if (!Number.isNaN(fromPub) && fromPub > 0) {
        return fromPub;
      }
    }
    const pathParts = path.split("/").filter(Boolean);
    for (let i = pathParts.length - 1; i >= 0; i--) {
      const segId = Number.parseInt(pathParts[i], 10);
      if (!Number.isNaN(segId) && segId > 0) {
        return segId;
      }
    }
    const urlParams = new URLSearchParams(window.location.search);
    const qs =
      urlParams.get("experience_id") ||
      urlParams.get("expid") ||
      urlParams.get("id");
    return qs ? Number.parseInt(qs, 10) : null;
  }

  function getConfiguredMappedDomains(): string[] {
    if (!Array.isArray(window.MAPPED_DOMAINS)) {
      return [];
    }
    return window.MAPPED_DOMAINS.map(domain =>
      String(domain || "")
        .trim()
        .toLowerCase(),
    ).filter(Boolean);
  }

  function getExperienceMapUrl(): string | null {
    const mappedDomains = getConfiguredMappedDomains();
    if (mappedDomains.length === 0) {
      return null;
    }

    function toMappedUrl(candidate: string | null): string | null {
      if (!candidate) {
        return null;
      }
      let parsed;
      try {
        parsed = new URL(candidate, window.location.origin);
      } catch {
        return null;
      }
      const hostname = parsed.hostname.toLowerCase();
      const isMapped = mappedDomains.some(function (domain) {
        return hostname === domain || hostname.endsWith(`.${domain}`);
      });
      return isMapped ? parsed.toString() : null;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const fromQuery = toMappedUrl(urlParams.get("url"));
    if (fromQuery) {
      return fromQuery;
    }

    // Fallback: when player script runs directly on a mapped publisher page,
    // use the current page URL as the mapping key.
    return toMappedUrl(window.location.href);
  }

  function resolveExperienceTarget(): {
    experienceId: number | null;
    mapUrl: string | null;
  } {
    const experienceId = getExperienceIdFromUrl();
    if (experienceId) {
      return { experienceId: experienceId, mapUrl: null };
    }
    const mapUrl = getExperienceMapUrl();
    if (mapUrl) {
      return { experienceId: null, mapUrl: mapUrl };
    }
    return { experienceId: null, mapUrl: null };
  }

  /** Keep in sync with experienceMediaUrls.ts#hostUsesMediaProxy */
  function hostUsesMediaProxy(hostname: string): boolean {
    let h = String(hostname || "").toLowerCase();
    const colon = h.indexOf(":");
    if (colon >= 0 && h.indexOf("[") < 0) {
      h = h.slice(0, colon);
    }
    if (!h) {
      return false;
    }
    if (h === "localhost" || h === "127.0.0.1") {
      return true;
    }
    if (h === "automaze.dev" || h.endsWith(".automaze.dev")) {
      return true;
    }
    if (h === "play.pixmoto.loc" || h.endsWith(".play.pixmoto.loc")) {
      return true;
    }
    return false;
  }

  function defaultMediaServerHost(): string {
    if (window.ENVIRONMENT === "production") {
      return "media.pixmoto.com";
    }
    const hostname = window.location.hostname || window.location.host;
    if (hostUsesMediaProxy(hostname)) {
      return `${window.location.host}/proxy/media.pixmoto.com`;
    }
    return "media.pixmoto.com";
  }

  /**
   * Fetch client IP. Returns Promise<string> (empty string on failure).
   */
  function fetchClientIp(): Promise<string> {
    return fetch(IPIFY_URL, { method: "GET" })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(d => d.ip || "")
      .catch(() => "");
  }

  /**
   * Build experienceMetadata object (does not set window.pixmoto.experienceMetadata).
   */
  function buildExperienceMetadata(
    opts: MetadataOptions,
  ): ExperienceMetadataInit {
    const id = opts.experienceId;
    const mapUrl = opts.mapUrl || "";
    const experienceUrl = mapUrl
      ? `${opts.apiProtocol}://${opts.apiBaseUrl}/publisher/experience?url=${encodeURIComponent(mapUrl)}`
      : `${opts.apiProtocol}://${opts.apiBaseUrl}/publisher/experience/${id}`;
    return {
      pixmotoUID: "174196",
      assetServer: resolveAssetServer(opts.assetServer),
      mediaServer: resolveMediaServer(opts.mediaServer),
      apiServer: opts.apiBaseUrl,
      sharedServer: "cdn.pixmoto.com",
      idexperience: id,
      url: mapUrl,
      experienceUrl,
      ipAddress: opts.ipAddress || "",
    };
  }

  /**
   * Reloads the page once per `?v=` token of `src`; false when it already did or storage
   * is unavailable. A failed module URL cannot load again in the same page.
   */
  function reloadOnce(src: string): boolean {
    // The hidden warm iframe must not use up the one reload the visible player may need.
    if (/[?&]pix-warm-only=1(&|$)/.test(global.location.search)) {
      return false;
    }
    const token = /[?&]v=([^&]*)/.exec(src);
    const key = `pix-play-reload:${token ? token[1] : src}`;
    try {
      if (global.sessionStorage.getItem(key)) {
        return false;
      }
      global.sessionStorage.setItem(key, "1");
    } catch {
      return false;
    }
    global.location.reload();
    return true;
  }

  /**
   * Load scripts in order (each { src, await, module }). await: true means load before next.
   */
  function loadScripts(scripts: BootScript[], done?: () => void) {
    let i = 0;
    function next() {
      if (i >= scripts.length) {
        if (done) {
          done();
        }
        return;
      }
      const item = scripts[i++];
      const s = document.createElement("script");
      if (item.module) {
        s.type = "module";
        // A module script fetches at High; Low matches the old async classic play.js.
        s.fetchPriority = "low";
      }
      s.src = item.src;
      if (item.await) {
        s.setAttribute("await", "");
      }
      s.onload = next;
      s.onerror = () => {
        if (!(item.module && reloadOnce(item.src))) {
          next();
        }
      };
      document.body.appendChild(s);
    }
    next();
  }

  /**
   * Fetch client IP, set experienceMetadata, then load scripts.
   * options: { experienceId, mediaServer, apiBaseUrl, apiProtocol, scripts[] }
   * scripts: [ { src: "...", await: true|false, module: true|false }, ... ]
   * Returns Promise that resolves when scripts have been loaded.
   */
  function bootPlayer(options: BootOptions): Promise<void> {
    const mediaServer = resolveMediaServer(options.mediaServer);
    const assetServer = resolveAssetServer(options.assetServer);
    const apiBaseUrl =
      options.apiBaseUrl ||
      window.PIXMOTO_API_BASE_URL ||
      "admin-api.pixmoto.com";
    const apiProtocol =
      options.apiProtocol || window.PIXMOTO_API_PROTOCOL || "https";
    const scripts = options.scripts || [];

    if (!window.pixmoto) {
      window.pixmoto = {};
    }
    const pixmoto = window.pixmoto;

    return fetchClientIp().then(ipAddress => {
      pixmoto.experienceMetadata = buildExperienceMetadata({
        experienceId: options.experienceId,
        mapUrl: options.mapUrl,
        ipAddress: ipAddress,
        assetServer: assetServer,
        mediaServer: mediaServer,
        apiBaseUrl: apiBaseUrl,
        apiProtocol: apiProtocol,
      });
      return new Promise<void>(resolve => {
        loadScripts(scripts, resolve);
      });
    });
  }

  global.PixmotoSampleUtils = {
    getExperienceIdFromUrl: getExperienceIdFromUrl,
    getExperienceMapUrl: getExperienceMapUrl,
    resolveExperienceTarget: resolveExperienceTarget,
    fetchClientIp: fetchClientIp,
    resolveAssetServer: resolveAssetServer,
    resolveMediaServer: resolveMediaServer,
    buildExperienceMetadata: buildExperienceMetadata,
    bootPlayer: bootPlayer,
    // The pages read it: an older cached loader lacks it and cannot load play.js as a module.
    modules: true,
  };
})(window);
